import type { CustomerInvoiceListDto } from '@orus.eu/backend'

import { type InvoiceStatus } from '#/modules/invoice/front-office/shared/invoice-status.types.ts'

export type InvoiceListEntry = {
  invoice: CustomerInvoiceListDto
  status: InvoiceStatus
}

type InvoicesPageContent = {
  /** Soonest upcoming (`new`) invoice, highlighted in the next-invoice card. */
  nextInvoice: CustomerInvoiceListDto | undefined
  /** Every invoice with a downloadable document, most recent first. */
  history: InvoiceListEntry[]
  isEmpty: boolean
}

export function getInvoicesPageContent(invoices: CustomerInvoiceListDto[]): InvoicesPageContent {
  const newInvoices = invoices.filter((invoice) => invoice.paymentStatus === 'new')
  const nextInvoice = [...newInvoices].sort((a, b) => a.invoice.issueTimestamp - b.invoice.issueTimestamp)[0]

  const history = [...invoices]
    .sort((a, b) => b.invoice.dueTimestamp - a.invoice.dueTimestamp)
    .flatMap((invoice) => {
      const status = getInvoiceStatus(invoice)
      return status === undefined ? [] : [{ invoice, status }]
    })

  return { nextInvoice, history, isEmpty: nextInvoice === undefined && history.length === 0 }
}

/** `undefined` when the invoice has no document the customer can download, and so nothing to list. */
function getInvoiceStatus(invoice: CustomerInvoiceListDto): InvoiceStatus | undefined {
  if (invoice.receiptDocumentAvailable) return 'paid'
  if (!invoice.invoiceDocumentAvailable) return undefined
  if (invoice.paymentStatus === 'failed' || invoice.paymentStatus === 'disputed') return 'unpaid'
  if (invoice.paymentStatus === 'pending') return 'pending'
  return undefined
}
